import {useSafeChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {referenceID} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {useQuery} from '@github-ui/react-query'
import {useEffect} from 'react'

import type {FileDiff} from '../components/ContentPreview/content-preview-types'
import type {ContentPreviewContext} from '../components/ContentPreview/ContentPreviewContext'

export function useHydrateDiffFile(
  fileDiff: FileDiff,
  updateItem: ContentPreviewContext['updateItem'],
  fileType: 'base' | 'head',
) {
  const targetFile = fileType === 'head' ? fileDiff.headFile : fileDiff.baseFile
  const reference = targetFile.reference
  const manager = useSafeChatManager()
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const {isLoading, isError, data} = useQuery({
    queryKey: [
      'copilot-reference-preview',
      'copilot',
      'references',
      'file',
      reference ? referenceID(reference) : undefined,
      fileType,
    ],
    queryFn: async () => {
      if (!manager || !reference || reference.type !== 'file' || targetFile.value !== '') return null

      const result = await manager.service.hydrateReference(reference)
      if (!result.ok) return null

      const updatedFileDiff = {
        ...fileDiff,
        [fileType === 'head' ? 'headFile' : 'baseFile']: {
          ...targetFile,
          value: result.payload.contents,
          isStreaming: false,
        },
      }
      updateItem(updatedFileDiff)
      return result.payload
    },
    // references include an OID and are thus not going to change ever
    staleTime: Number.POSITIVE_INFINITY,
    // Without a chat manager there is nothing to hydrate from. Skip the query rather than caching a
    // null result, which staleTime: Infinity would keep fresh and hide a later chat-hosted fetch.
    enabled: !!manager,
  })

  useEffect(() => {
    // if the query is cached we might still need to run updateItem
    if (data?.contents && targetFile.value !== data.contents) {
      const updatedFileDiff = {
        ...fileDiff,
        [fileType === 'head' ? 'headFile' : 'baseFile']: {
          ...targetFile,
          value: data.contents,
          isStreaming: false,
        },
      }
      updateItem(updatedFileDiff)
    }
  }, [data?.contents, fileDiff, updateItem, targetFile, fileType])

  return {isLoading, isError, data}
}
